import { useCallback } from 'react';
import { Grid, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { DocumentTitle } from '@client/components/common/DocumentTitle';
import { getDateRange } from '@sda/utils/date';
import { getEventLocation, getRoundedFollowers, pluralize } from '@sda/utils/string';
import { useDispatch, useSelector } from '@client/hooks/redux.hooks';
import {
  getMapHitTotal, getGroupedMapHits, getMapLoaded, getMapStatus,
} from '@client/selectors/features/map';
import { setHoverId } from '@client/slices/features/map.reducer';
import { ConnectCard } from '../common/ConnectCard';
import { CreateShowCallButton } from '../common/CreateShowCallButton';
import { EmptyResults } from './EmptyResults';
import { ConnectMap } from './ConnectMap';
import { FilterBar } from './FilterBar';
import { ConnectCardGroup } from './ConnectCardGroup';

const useStyles = makeStyles()((theme) => ({
  root: {
    height: '100vh',
  },
  gridContainer: {
    height: '100%',
  },
  connectListContainer: {
    width: 730,
    display: 'flex',
    height: '100%',

    '& > div': {
      width: '100%',
      overflowY: 'auto',
    },
  },
  toolbar: {
    marginBottom: theme.spacing(3),
  },
  connectList: {
    padding: theme.spacing(2, 3),
  },
  mapContainer: {
    position: 'relative',
  },
}));

export const Desktop = () => {
  const { classes, cx } = useStyles();
  const dispatch = useDispatch();

  const nbHits = useSelector(getMapHitTotal);
  const hits = useSelector(getGroupedMapHits);

  const isLoaded = useSelector(getMapLoaded);
  const status = useSelector(getMapStatus);

  const handleMouseEnter = useCallback((hoverId: string) => {
    dispatch(setHoverId(hoverId));
  }, [dispatch]);

  return (
    <div className={classes.root}>
      <DocumentTitle title="Connect" />

      <Grid
        className={classes.gridContainer}
        container
      >
        <Grid
          className={classes.connectListContainer}
          item
        >
          <div className={classes.connectList}>
            {Boolean(isLoaded) && Boolean(status) && (
              <>
                {!nbHits ? <EmptyResults /> : (
                  <>
                    <Grid
                      container
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={3}
                      className={classes.toolbar}
                    >
                      <Grid item>
                        <FilterBar />
                      </Grid>

                      <Grid item>
                        <Typography
                          variant="body2"
                          color="disabled"
                        >
                          {pluralize('Show Call', nbHits)}
                        </Typography>
                      </Grid>
                    </Grid>

                    <Grid
                      spacing={3}
                      container
                    >
                      {hits.map((hitSet) => {
                        if (hitSet.hits.length > 1) {
                          return (
                            <ConnectCardGroup
                              key={hitSet.profileId}
                              hitSet={hitSet.hits}
                              onMouseEnter={handleMouseEnter}
                            />
                          );
                        }

                        const [hit] = hitSet.hits;

                        return (
                          <Grid
                            key={hit.objectID}
                            xs={6}
                            item
                          >
                            <ConnectCard
                              chipLabel={!hit.publicBeacon.isPublicCall && 'Private'}
                              imgSrc={hit.publicBeacon.profileData.publicImageUrl}
                              path={`/connect/${hit.objectID}`}
                              title={hit.publicBeacon.profileData.name}
                              location={getEventLocation(hit.publicBeacon)}
                              numReviews={hit.publicBeacon.profileData.numReviews}
                              score={hit.publicBeacon.profileData.score}
                              dateRange={getDateRange(hit.publicBeacon)}
                              isFeatured={hit.publicBeacon.isFeatured}
                              badge={hit.publicBeacon.profileData.badge}
                              spotifyMonthlyActiveListeners={getRoundedFollowers(hit.publicBeacon.profileData.spotifyMonthlyActiveListeners, 'compact')}
                              onMouseEnter={() => handleMouseEnter(hit.objectID)}
                              onMouseLeave={() => handleMouseEnter(null)}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </>
                )}
              </>
            )}
          </div>
        </Grid>

        <Grid
          className={cx('spacer', classes.mapContainer)}
          item
          zeroMinWidth
        >
          <ConnectMap />

          <CreateShowCallButton />
        </Grid>
      </Grid>
    </div>
  );
};

export default Desktop;
